import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

export default function BlogAdminPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', cover_image: '', seo_title: '', seo_description: '', status: 'draft' });

  const loadData = () => {
    setLoading(true);
    adminAPI.getBlog().then(r => setPosts(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setForm({ title: '', content: '', cover_image: '', seo_title: '', seo_description: '', status: 'draft' });
    setEditing(null);
    setShowForm(false);
  };

  const startEdit = (p) => {
    setForm({ title: p.title, content: p.content || '', cover_image: p.cover_image || '', seo_title: p.seo_title || '', seo_description: p.seo_description || '', status: p.status });
    setEditing(p.id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Baslik zorunlu'); return; }
    try {
      if (editing) {
        await adminAPI.updateBlog(editing, form);
        toast.success('Yazi guncellendi');
      } else {
        await adminAPI.createBlog(form);
        toast.success('Yazi olusturuldu');
      }
      resetForm();
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Silinsin mi?')) return;
    try { await adminAPI.deleteBlog(id); toast.success('Silindi'); loadData(); }
    catch { toast.error('Silinemedi'); }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Blog Yazilari</h1>
        <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-blog-btn">
          <Plus className="h-4 w-4 mr-2" /> Yeni Yazi
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editing ? 'Yazi Duzenle' : 'Yeni Blog Yazisi'}</CardTitle>
            <button onClick={resetForm}><X className="h-5 w-5" /></button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Baslik *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} data-testid="blog-title-input" /></div>
            <div><Label>Icerik (HTML)</Label><textarea value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} rows={8} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" /></div>
            <div><Label>Kapak Gorseli URL</Label><Input value={form.cover_image} onChange={e => setForm(f => ({...f, cover_image: e.target.value}))} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>SEO Title</Label><Input value={form.seo_title} onChange={e => setForm(f => ({...f, seo_title: e.target.value}))} /></div>
              <div><Label>SEO Description</Label><Input value={form.seo_description} onChange={e => setForm(f => ({...f, seo_description: e.target.value}))} /></div>
            </div>
            <div>
              <Label>Durum</Label>
              <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="draft">Taslak</option>
                <option value="published">Yayinda</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} data-testid="blog-submit">{editing ? 'Guncelle' : 'Kaydet'}</Button>
              <Button variant="outline" onClick={resetForm}>Iptal</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : posts.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Henuz blog yazisi yok</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {posts.map(p => (
            <Card key={p.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{p.title}</span>
                    <Badge variant={p.status === 'published' ? 'default' : 'secondary'} className="text-xs">{p.status === 'published' ? 'Yayinda' : 'Taslak'}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">/blog/{p.slug}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(p)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
